import { Code, Table } from '@mantine/core'
import LinkButton from 'common/LinkButton'
import dayjs from 'dayjs'
import { AuditAction, AuditLogTableItem, AuditType } from 'types/audit'
import { Category, PillowType, Unit } from 'types/products'
import { getAttributeLabel, getAuditTypeName, getCategoryName, getMessageForAuditAction, getUnitName } from 'utils'

type Props = {
  auditLog: AuditLogTableItem
  closeModal: () => void
}

export default function AuditLogDetails({ closeModal, auditLog }: Props) {
  const { changes, createdAt, auditType, auditAction, user, product, variant, metadata } = auditLog

  const hasOldValue = changes?.some((change) => change.oldValue)
  const hasNewValue = changes?.some((change) => change.newValue)

  return (
    <>
      <Table variant="vertical" withTableBorder>
        <Table.Tbody>
          {user && (
            <Table.Tr>
              <Table.Th>Użytkownik</Table.Th>
              <Table.Td>{user.fullName}</Table.Td>
            </Table.Tr>
          )}
          {product && (
            <Table.Tr>
              <Table.Th>Symbol produktu</Table.Th>
              <Table.Td>
                {product.id ? (
                  <LinkButton onClick={() => closeModal()} to="/product/$productId" params={{ productId: product.id }}>
                    {product.symbol}
                  </LinkButton>
                ) : (
                  product.symbol
                )}
              </Table.Td>
            </Table.Tr>
          )}
          {variant && (
            <Table.Tr>
              <Table.Th>Wariant</Table.Th>
              <Table.Td>
                {product && product.id && variant.id ? (
                  <LinkButton
                    onClick={() => {
                      if (product) closeModal()
                    }}
                    to="/product/$productId/$category"
                    params={{ productId: product.id, category: variant.category }}
                    search={{ variant: variant.id }}
                  >
                    {getCategoryName(variant.category, { capitalize: true, short: true }).mianownik} | Półka:{' '}
                    {variant.place} | {getSizeLabel(variant.category)}: {variant.size}
                  </LinkButton>
                ) : (
                  <>
                    {getCategoryName(variant.category, { capitalize: true, short: true }).mianownik} | Półka:{' '}
                    {variant.place} | {getSizeLabel(variant.category)}: {variant.size}
                  </>
                )}
              </Table.Td>
            </Table.Tr>
          )}
          <Table.Tr>
            <Table.Th>Data</Table.Th>
            <Table.Td>{dayjs(createdAt).format('D MMMM HH:mm')}</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Th>Typ akcji</Table.Th>
            <Table.Td>{getAuditTypeName(auditType as AuditType)}</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Th>Wiadomość</Table.Th>
            <Table.Td>{getMessageForAuditAction(auditAction as AuditAction)}</Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
      {changes && (
        <Table mt="lg">
          <Table.Thead>
            <Table.Tr>
              <Table.Th></Table.Th>
              {hasOldValue && <Table.Th>Stara wartość</Table.Th>}
              {hasNewValue && <Table.Th>Nowa wartość</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {changes.map((change) => {
              if (change.field === 'PillowType') {
                return (
                  <Table.Tr key={change.field}>
                    <Table.Td>{getAttributeLabel(change.field)}</Table.Td>
                    {hasOldValue && (
                      <Table.Td>
                        {change.oldValue === PillowType.FABRIC
                          ? 'Materiał'
                          : change.oldValue === PillowType.PREMADE
                            ? 'Gotowa'
                            : ''}
                      </Table.Td>
                    )}
                    {hasNewValue && (
                      <Table.Td>
                        {change.newValue === PillowType.FABRIC
                          ? 'Materiał'
                          : change.newValue === PillowType.PREMADE
                            ? 'Gotowa'
                            : ''}
                      </Table.Td>
                    )}
                  </Table.Tr>
                )
              }
              if (change.field === 'Category') {
                return (
                  <Table.Tr key={change.field}>
                    <Table.Td>{getAttributeLabel(change.field)}</Table.Td>
                    {hasOldValue && (
                      <Table.Td>
                        {change.oldValue
                          ? getCategoryName(change.oldValue as Category, { capitalize: true, short: true }).mianownik
                          : ''}
                      </Table.Td>
                    )}
                    {hasNewValue && (
                      <Table.Td>
                        {change.newValue
                          ? getCategoryName(change.newValue as Category, { capitalize: true, short: true }).mianownik
                          : ''}
                      </Table.Td>
                    )}
                  </Table.Tr>
                )
              }
              if (change.field === 'Unit') {
                return (
                  <Table.Tr key={change.field}>
                    <Table.Td>{getAttributeLabel(change.field)}</Table.Td>
                    {hasOldValue && (
                      <Table.Td>{change.oldValue ? getUnitName(change.oldValue as Unit).mianownik : ''}</Table.Td>
                    )}
                    {hasNewValue && (
                      <Table.Td>{change.newValue ? getUnitName(change.newValue as Unit).mianownik : ''}</Table.Td>
                    )}
                  </Table.Tr>
                )
              }
              if (change.field === 'Image') {
                return (
                  <Table.Tr key={change.field}>
                    <Table.Td>{getAttributeLabel(change.field)}</Table.Td>
                    {hasOldValue && (
                      <Table.Td>{change.oldValue ? shortenText(getImageName(change.oldValue), 18) : ''}</Table.Td>
                    )}
                    {hasNewValue && (
                      <Table.Td>{change.newValue ? shortenText(getImageName(change.newValue), 18) : ''}</Table.Td>
                    )}
                  </Table.Tr>
                )
              }
              return (
                <Table.Tr key={change.field}>
                  <Table.Td>{getAttributeLabel(change.field)}</Table.Td>
                  {hasOldValue && <Table.Td>{getPossibleConstants(change.oldValue)}</Table.Td>}
                  {hasNewValue && <Table.Td>{getPossibleConstants(change.newValue)}</Table.Td>}
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      )}
      {metadata && (
        <Code mt="lg" block>
          {JSON.stringify(metadata, null, 2)}
        </Code>
      )}
    </>
  )
}

function getSizeLabel(category: Category) {
  switch (category) {
    case Category.FABRIC:
      return 'Wysokość'
    default:
      return 'Wymiar'
  }
}

function getImageName(url: string): string {
  return url.split('/').pop() || url
}

function shortenText(text: string, maxLength: number) {
  // shorten text to maxLength characters - ...suffix, suffix = maxLength
  if (text.length <= maxLength) return text
  return '...' + text.slice(-maxLength + 3)
}

function getPossibleConstants(value: string) {
  switch (value) {
    case 'true':
      return 'Tak'
    case 'false':
      return 'Nie'
    default:
      return value
  }
}
